import { useRef } from "react";
import {
  useBulkDeleteManualRows,
  useBulkPasteAiSpreadsheet,
  useBulkRestoreManualRows,
  useConfirmAiSpreadsheetRegionOverride,
  useCreateAiSpreadsheetRegion,
  useCreateManualRow,
  useDeleteAiSpreadsheetRegion,
  useDeleteManualRow,
  useDirectUpdateAiSpreadsheetCell,
  useRestoreManualRow,
  useUpdateAiSpreadsheetCell,
  useUpdateAiSpreadsheetColumnValueType,
  useUpdateAiSpreadsheetRegion,
} from "@/payroll/ai-script/query";
import { createCellEditBatchFeedbackCollector } from "./cell-edit-feedback";

export const useViewerMutations = (businessId: string | null) => {
  const { mutateAsync: updateCellAsync, isPending: isUpdatingCell } =
    useUpdateAiSpreadsheetCell(businessId);
  const {
    mutateAsync: directUpdateCellAsync,
    isPending: isUpdatingCellDirectly,
  } = useDirectUpdateAiSpreadsheetCell(businessId);
  const isUpdatingAnyCell = isUpdatingCell || isUpdatingCellDirectly;
  const cellEditFeedbackCollectorRef = useRef(
    createCellEditBatchFeedbackCollector(),
  );
  const {
    mutateAsync: updateColumnValueTypeAsync,
    isPending: isUpdatingColumnValueType,
  } = useUpdateAiSpreadsheetColumnValueType(businessId);
  const { mutateAsync: bulkPasteAsync, isPending: isBulkPasting } =
    useBulkPasteAiSpreadsheet(businessId);
  const createSpreadsheetRegion = useCreateAiSpreadsheetRegion(businessId);
  const updateSpreadsheetRegion = useUpdateAiSpreadsheetRegion(businessId);
  const deleteSpreadsheetRegion = useDeleteAiSpreadsheetRegion(businessId);
  const confirmSpreadsheetRegionOverride =
    useConfirmAiSpreadsheetRegionOverride(businessId);
  const {
    mutate: createManualRow,
    mutateAsync: createManualRowAsync,
    isPending: isCreatingManualRow,
  } = useCreateManualRow(businessId);
  const { mutate: deleteManualRow, mutateAsync: deleteManualRowAsync } =
    useDeleteManualRow(businessId);
  const { mutateAsync: restoreManualRowAsync } =
    useRestoreManualRow(businessId);
  const { mutateAsync: bulkDeleteRowsAsync } =
    useBulkDeleteManualRows(businessId);
  const { mutateAsync: bulkRestoreRowsAsync } =
    useBulkRestoreManualRows(businessId);

  return {
    updateCellAsync,
    directUpdateCellAsync,
    isUpdatingAnyCell,
    cellEditFeedbackCollectorRef,
    updateColumnValueTypeAsync,
    isUpdatingColumnValueType,
    bulkPasteAsync,
    isBulkPasting,
    createSpreadsheetRegion,
    updateSpreadsheetRegion,
    deleteSpreadsheetRegion,
    confirmSpreadsheetRegionOverride,
    createManualRow,
    createManualRowAsync,
    isCreatingManualRow,
    deleteManualRow,
    deleteManualRowAsync,
    restoreManualRowAsync,
    bulkDeleteRowsAsync,
    bulkRestoreRowsAsync,
  };
};

export type ViewerMutations = ReturnType<typeof useViewerMutations>;
